import { useCallback, useMemo } from 'react';
import { ContentCollectionType } from '../../API';
import { useData } from '../../data-layer';
import { useRecoilState } from 'recoil';
import { CollectionFilterOptions, CollectionSearchParams } from '../../components/Collections/types';
import { useLocales } from '..';
import { AppRoutes } from '../../Routes';

type ReturnType = {
  isMixed: boolean;
  isTop10: boolean;
  isVOD: boolean;
  reset: () => void;
  getCollectionsBrowserActions: () => Record<string, string>;
  initialize: (customCollectionType?: ContentCollectionType) => void;
  updateLastSavedSearchParams: (params: CollectionSearchParams) => void;
  getLastSavedSearchParams: () => CollectionSearchParams | undefined;
  getAppRoute: (entityId?: string, collectionType?: ContentCollectionType) => string;
  getContentCollectionTypes: () => ContentCollectionType[];
  getContentCollectionType: () => ContentCollectionType | undefined;
  getCollectionFormCopy: (isNew?: boolean) => string;
};

export function useCollectionDisplay(collectionType?: ContentCollectionType): ReturnType {
  const {
    collections: {
      state: { withTop10CollectionDisplay, withMixedCollectionDisplay, withLastSavedSearchParams }
    }
  } = useData();
  const { t } = useLocales();

  const [lastSavedSearchParams, setLastSavedSearchParams] = useRecoilState(withLastSavedSearchParams);
  const [top10CollectionDisplay, setTop10CollectionDisplay] = useRecoilState(withTop10CollectionDisplay);
  const [mixedCollectionDisplay, setMixedCollectionDisplay] = useRecoilState(withMixedCollectionDisplay);

  const initialize = useCallback(
    (customCollectionType?: ContentCollectionType) => {
      const collectionTypeToUse = customCollectionType ?? collectionType;
      if (collectionTypeToUse === ContentCollectionType.MIXED_LIST) {
        setMixedCollectionDisplay(true);
        setTop10CollectionDisplay(false);
      } else if (collectionTypeToUse === ContentCollectionType.TOP_10) {
        setTop10CollectionDisplay(true);
        setMixedCollectionDisplay(false);
      } else {
        setTop10CollectionDisplay(false);
        setMixedCollectionDisplay(false);
      }
    },
    [collectionType]
  );

  const reset = useCallback(() => {
    setTop10CollectionDisplay(false);
    setMixedCollectionDisplay(false);
  }, [setTop10CollectionDisplay, setMixedCollectionDisplay]);

  const getCollectionsBrowserActions = useCallback(() => {
    const filterOptionsToExclude = mixedCollectionDisplay
      ? [CollectionFilterOptions.HAS_SMART_QUERY, CollectionFilterOptions.FOR_SIMILARS]
      : [CollectionFilterOptions.HAS_ASSETS_THESE_TYPES];

    const actions: Record<string, string> = Object.values(CollectionFilterOptions)
      .filter((option) => !filterOptionsToExclude.includes(option))
      .reduce(
        (result, option) => ({
          ...result,
          [option]: t(`collections.advanced_search.${option}.name`)
        }),
        {}
      );

    return actions;
  }, [mixedCollectionDisplay]);

  const updateLastSavedSearchParams = useCallback(
    (params: CollectionSearchParams) => {
      let additionalParams: Record<string, CollectionSearchParams | undefined> = {
        mixedCollection: undefined,
        top10Collection: undefined,
        vodCollection: undefined
      };

      if (mixedCollectionDisplay) {
        additionalParams = { mixedCollection: params };
      } else if (top10CollectionDisplay) {
        additionalParams = { top10Collection: params };
      } else {
        additionalParams = { vodCollection: params };
      }

      setLastSavedSearchParams({
        ...lastSavedSearchParams,
        ...additionalParams
      });
    },
    [lastSavedSearchParams, setLastSavedSearchParams, mixedCollectionDisplay, top10CollectionDisplay]
  );

  const getLastSavedSearchParams = useCallback(() => {
    if (mixedCollectionDisplay) {
      return lastSavedSearchParams.mixedCollection;
    }
    if (top10CollectionDisplay) {
      return lastSavedSearchParams.top10Collection;
    }
    return lastSavedSearchParams.vodCollection;
  }, [lastSavedSearchParams, mixedCollectionDisplay, top10CollectionDisplay]);

  const getAppRoute = useCallback(
    (entityId?: string, collectionType?: ContentCollectionType) => {
      if (collectionType === ContentCollectionType.MIXED_LIST || (!collectionType && mixedCollectionDisplay)) {
        return AppRoutes.collections(entityId);
      }
      if (collectionType === ContentCollectionType.TOP_10 || (!collectionType && top10CollectionDisplay)) {
        return AppRoutes.top10Collections(entityId);
      }
      return AppRoutes.vodCollections(entityId);
    },
    [mixedCollectionDisplay, top10CollectionDisplay]
  );

  const getContentCollectionTypes = useCallback(() => {
    if (mixedCollectionDisplay) {
      return [ContentCollectionType.MIXED_LIST];
    }
    if (top10CollectionDisplay) {
      return [ContentCollectionType.TOP_10];
    }
    return Object.values(ContentCollectionType).filter(
      (t) => t !== ContentCollectionType.MIXED_LIST && t !== ContentCollectionType.TOP_10
    );
  }, [mixedCollectionDisplay, top10CollectionDisplay]);

  const getContentCollectionType = useCallback(() => {
    if (!collectionType) {
      if (mixedCollectionDisplay) {
        return ContentCollectionType.MIXED_LIST;
      }
      if (top10CollectionDisplay) {
        return ContentCollectionType.TOP_10;
      }
    }

    return collectionType;
  }, [collectionType, mixedCollectionDisplay, top10CollectionDisplay]);

  const getCollectionFormCopy = useCallback(
    (isNew = false) => {
      if (mixedCollectionDisplay) {
        return t(`collections.${isNew ? 'new_mixed_collection' : 'edit_mixed_collection_metadata'}`);
      }
      if (top10CollectionDisplay) {
        return t(`collections.${isNew ? 'new_top_10_collection' : 'edit_top_10_collection_metadata'}`);
      }
      return t(`collections.${isNew ? 'new_collection' : 'edit_collection_metadata'}`);
    },
    [mixedCollectionDisplay, top10CollectionDisplay]
  );

  const isMixed = useMemo(() => {
    return mixedCollectionDisplay;
  }, [mixedCollectionDisplay]);

  const isTop10 = useMemo(() => {
    return top10CollectionDisplay;
  }, [top10CollectionDisplay]);

  const isVOD = useMemo(() => {
    return !isMixed && !isTop10;
  }, [isMixed, isTop10]);

  return {
    isMixed,
    isTop10,
    isVOD,
    reset,
    getCollectionsBrowserActions,
    initialize,
    updateLastSavedSearchParams,
    getLastSavedSearchParams,
    getAppRoute,
    getContentCollectionTypes,
    getContentCollectionType,
    getCollectionFormCopy
  };
}
